import React, {useRef, useEffect, useCallback} from 'react';
import {observer} from 'mobx-react-lite';
import {reaction, IReactionDisposer, set} from 'mobx';
import {InteractiveElement} from '../../utils/InteractiveElement';
import {CompositionStateStore} from '../../../../store/CompositionStateStore';
import {WindowStore} from '../../../../store/WindowStore';
import {ApplicationStore, VisualElementRef} from '../../../../store/ApplicationStore';
import CanvasGuidelinesManager from "../../managers/CanvasGuidelinesManager";
import CanvasAlignmentGuidelinesContainer
    from "../CanvasAlignmentGuidelinesContainer/CanvasAlignmentGuidelinesContainer";
import {CompositionService} from "../../../../services/CompositionService";
import {ScriptLayerClip} from "../../../../../engine/clips/script/script";
import {ScriptLayerStore} from "../../../../store/ScriptLayerStore";

/**
 * Get the effective scene index for a ScriptLayerClip.
 * Uses selectedSceneIndex if set, otherwise finds scene from playhead position.
 */
function getEffectiveSceneIndex(clip: ScriptLayerClip): number | null {
    // Use explicitly selected scene if set
    if (ApplicationStore.selectedSceneIndex !== null) {
        return ApplicationStore.selectedSceneIndex;
    }

    // Otherwise, find scene from current playhead position
    const currentTime = CompositionStateStore.currentTime;
    const scenes = clip.getScenes();
    if (!scenes || scenes.length === 0) return null;

    // Find the scene at the current playhead
    const sceneAtPlayhead = scenes.find(scene =>
        currentTime >= scene.timelineStartSec && currentTime < scene.timelineEndSec
    );

    return sceneAtPlayhead?.index ?? null;
}

/**
 * Helper to get the visual element (view or sprite) based on selectedVisualElement
 */
function getVisualTarget(
    selectedVisualElement: VisualElementRef | null
): { clip: any; view: any; isScriptLayerSource: boolean; sourceIndex?: number } | null {
    if (!selectedVisualElement) return null;
    
    const clip = CompositionService.getClipById(selectedVisualElement.clipId);
    if (!clip) return null;
    
    // Check if this is a ScriptLayerClip with a specific source selected
    if (clip instanceof ScriptLayerClip && selectedVisualElement.sourceIndex !== undefined) {
        const sprite = clip.getVideoSprite(selectedVisualElement.sourceIndex);
        if (!sprite) return null;
        return { 
            clip, 
            view: sprite, 
            isScriptLayerSource: true, 
            sourceIndex: selectedVisualElement.sourceIndex 
        };
    }
    
    // Regular clip - use the clip's view
    const view = (clip as any).view;
    if (!view) return null;
    return { clip, view, isScriptLayerSource: false };
}

	const DragResizeRotateContainer: React.FC = observer(() => {
	    const containerRef = useRef<HTMLDivElement>(null);
	    const interactionRef = useRef<InteractiveElement | null>(null);
	    const pixelRatioRef = useRef<number>(1);
	    const clearedAllScopeDragRef = useRef<boolean>(false);
	    const clearedAllScopeScaleRef = useRef<boolean>(false);
        const handlersRef = useRef<{
            onDrag: (x: number, y: number) => void;
            onResize: (deltaWidth: number, deltaHeight: number) => void;
            onScale: (deltaWidth: number, deltaHeight: number) => void;
            onRotate: (rotation: number) => void;
            dragStart: () => void;
            dragEnd: () => void;
            resizeStart: () => void;
            resizeEnd: () => void;
            scaleStart: () => void;
            scaleEnd: () => void;
            rotateStart: () => void;
            rotateEnd: () => void;
        }>({
            onDrag: () => {},
            onResize: () => {},
            onScale: () => {},
            onRotate: () => {},
            dragStart: () => {},
            dragEnd: () => {},
            resizeStart: () => {},
            resizeEnd: () => {},
            scaleStart: () => {},
            scaleEnd: () => {},
            rotateStart: () => {},
            rotateEnd: () => {},
        });

    const dragOffsetRef = useRef([]);
    const canvasGuidelinesContainerRef = useRef<HTMLDivElement>(null);

    const canvasGuidelinesManagerRef = useRef(new CanvasGuidelinesManager());

    const getSelectedVisualElement = useCallback((): any => {
        if (ApplicationStore.selectedVisualElement == null) {
            return null;
        }
        const clip = CompositionService.getClipById(ApplicationStore.selectedVisualElement.clipId);
        if (!clip) return null;

        // Check if this is a ScriptLayerClip with a specific source selected
        if (clip instanceof ScriptLayerClip && ApplicationStore.selectedVisualElement.sourceIndex !== undefined) {
            const sprite = clip.getVideoSprite(ApplicationStore.selectedVisualElement.sourceIndex);
            if (!sprite) return null;
            return {
                clip,
                view: sprite,
                isScriptLayerSource: true,
                sourceIndex: ApplicationStore.selectedVisualElement.sourceIndex
            };
        }

        // Regular clip - use the clip's view
        const view = (clip as any).view;
        if (!view) return null;
        return { clip, view, isScriptLayerSource: false };
    }, []);

	    const handleDrag = (x: number, y: number) => {
	        const target = getSelectedVisualElement();
	        if (!target) return;
	        
	        const { clip, view, isScriptLayerSource, sourceIndex } = target;
	        const scope = ApplicationStore.sceneEditScope;
	        
	        // Use the same axis-aligned bounds as the overlay (important when rotated 90°/270°).
        // `view.width/height` are in local axes and do NOT swap when rotated, but the overlay
        // is positioned from `getBounds()` which DOES reflect rotation.
        let width: number = Number(view?.width ?? 0);
        let height: number = Number(view?.height ?? 0);
        try {
            const b = typeof view?.getBounds === 'function' ? view.getBounds() : null;
            const bw = Number(b?.width ?? Number.NaN);
            const bh = Number(b?.height ?? Number.NaN);
            if (Number.isFinite(bw) && bw > 0) width = bw;
            if (Number.isFinite(bh) && bh > 0) height = bh;
        } catch {}

        // Convert overlay (container-local) px → composition px using DOM rects for precise canvas origin
        const overlayEl = containerRef.current;
        const relativeEl = overlayEl?.parentElement as HTMLElement | null;
        const canvasEl = relativeEl?.querySelector('canvas') as HTMLCanvasElement | null;
        const pr = pixelRatioRef.current;
        if (!Number.isFinite(pr) || pr <= 0) return;
        let originX = 0, originY = 0;
        if (relativeEl && canvasEl) {
            const relRect = relativeEl.getBoundingClientRect();
            const canvasRect = canvasEl.getBoundingClientRect();
            originX = (canvasRect.left - relRect.left);
            originY = (canvasRect.top - relRect.top);
        }
        const localX = (x - originX) / pr;
        const localY = (y - originY) / pr;
        let newX = localX + (isFinite(width) ? width / 2 : 0);
        let newY = localY + (isFinite(height) ? height / 2 : 0);
        // Snap to 1px granularity in composition coordinates
        const snappedX = Math.round(newX);
        const snappedY = Math.round(newY);

        // Optional guideline snapping
        const vGuide = canvasGuidelinesManagerRef.current.getVerticalGuideline(snappedX, width || 0);
        const hGuide = canvasGuidelinesManagerRef.current.getHorizontalGuideline(snappedY, height || 0);
        // @ts-ignore
        canvasGuidelinesContainerRef.current?.SetVerticalGuidelines(vGuide != null ? [vGuide * pixelRatioRef.current] : []);
        // @ts-ignore
        canvasGuidelinesContainerRef.current?.SetHorizontalGuidelines(hGuide != null ? [hGuide * pixelRatioRef.current] : []);
        let finalX = snappedX;
        let finalY = snappedY;
        if (vGuide != null) finalX = canvasGuidelinesManagerRef.current.applyGuidelineSnapping(snappedX, width || 0, vGuide);
        if (hGuide != null) finalY = canvasGuidelinesManagerRef.current.applyGuidelineSnapping(snappedY, height || 0, hGuide);

	        try {
	            if (isScriptLayerSource && sourceIndex !== undefined) {
	                const scriptClip = clip as ScriptLayerClip;
	                const sceneIndex = getEffectiveSceneIndex(scriptClip);
	                const spansMultipleScenes = CompositionService.clipSpansMultipleScenes(scriptClip);
	                const useAllScope = scope === "all" && spansMultipleScenes;

	                if (useAllScope) {
	                    if (!clearedAllScopeDragRef.current) {
	                        try { scriptClip.clearSceneVisualOverrideKeysForSource(sourceIndex, ["x", "y"]); } catch {}
	                        clearedAllScopeDragRef.current = true;
	                    }
	                    scriptClip.updateVideoSourceTransform(sourceIndex, { x: finalX, y: finalY });
	                    ScriptLayerStore.bumpVisualPropsVersion();
	                } else if (sceneIndex !== null) {
	                    // Use lightweight drag method - updates config + PixiJS container directly
	                    // NO cache invalidation - commit happens in handleDragEnd
	                    scriptClip.updateSceneVisualPropertiesDrag(sceneIndex, sourceIndex, { x: finalX, y: finalY });
	                    ScriptLayerStore.bumpVisualPropsVersion(); // Lightweight - only updates sidebar (not waveforms)
	                    // Note: NOT calling bumpCutsVersion() during drag - that's the expensive one
	                } else {
	                    // Update source-level properties (no scene context)
	                    scriptClip.updateVideoSourceTransform(sourceIndex, { x: finalX, y: finalY });
	                }
	            } else {
	                const spansMultipleScenes = CompositionService.clipSpansMultipleScenes(clip);
	                const useSceneOverrides = scope === "scene" && spansMultipleScenes;
	                const useAllScope = scope === "all" && spansMultipleScenes;

	                if (useSceneOverrides) {
	                    const sceneKey = CompositionService.getCurrentSceneKey();
	                    if (sceneKey) {
	                        CompositionService.setSceneVisualOverridesForClip(clip, sceneKey, { x: finalX, y: finalY });
	                    } else {
	                        clip.x = finalX;
	                        clip.y = finalY;
	                    }
	                } else {
	                    if (useAllScope && !clearedAllScopeDragRef.current) {
	                        CompositionService.clearSceneVisualOverrideKeysForClip(clip, ["x", "y"]);
	                        clearedAllScopeDragRef.current = true;
	                    }
	                    // Regular clip - update x/y directly
	                    clip.x = finalX;
	                    clip.y = finalY;
	                }
	            }
            // Render the frame so visual moves on screen
            CompositionService.composition.computeFrame();
            handleUpdate();
        } catch {
        }
    };

    const handleScale = (deltaWidth: number, deltaHeight: number) => {
        const target = getSelectedVisualElement();
        if (!target) return;
	        
	        const { clip, view, isScriptLayerSource, sourceIndex } = target;
	        const scope = ApplicationStore.sceneEditScope;

        const pr = pixelRatioRef.current;
        if (!Number.isFinite(pr) || pr <= 0) return;
        
        // Moveable's event.delta is INCREMENTAL (frame-to-frame), not cumulative from start.
        // So we add the incremental delta to the CURRENT view size, not the initial size.
        const currentW = Number(view?.width ?? 0);
        const currentH = Number(view?.height ?? 0);
        
        const nextW = Math.max(1, Math.round(currentW + (deltaWidth / pr)));
        const nextH = Math.max(1, Math.round(currentH + (deltaHeight / pr)));

	        try {
	            if (isScriptLayerSource && sourceIndex !== undefined) {
	                const scriptClip = clip as ScriptLayerClip;
	                const sceneIndex = getEffectiveSceneIndex(scriptClip);
	                const spansMultipleScenes = CompositionService.clipSpansMultipleScenes(scriptClip);
	                const useAllScope = scope === "all" && spansMultipleScenes;

	                if (useAllScope) {
	                    if (!clearedAllScopeScaleRef.current) {
	                        try { scriptClip.clearSceneVisualOverrideKeysForSource(sourceIndex, ["width", "height"]); } catch {}
	                        clearedAllScopeScaleRef.current = true;
	                    }
	                    scriptClip.updateVideoSourceTransform(sourceIndex, { width: nextW, height: nextH });
	                    ScriptLayerStore.bumpVisualPropsVersion();
	                } else if (sceneIndex !== null) {
	                    // Use lightweight drag method - updates config + PixiJS container directly
	                    // NO cache invalidation - commit happens in handleScaleEnd
	                    scriptClip.updateSceneVisualPropertiesDrag(sceneIndex, sourceIndex, { width: nextW, height: nextH });
	                    ScriptLayerStore.bumpVisualPropsVersion(); // Lightweight - only updates sidebar (not waveforms)
	                    // Note: NOT calling bumpCutsVersion() during drag - that's the expensive one
	                } else {
	                    // Update source-level properties (no scene context)
	                    scriptClip.updateVideoSourceTransform(sourceIndex, { width: nextW, height: nextH });
	                }
	            } else {
                const spansMultipleScenes = CompositionService.clipSpansMultipleScenes(clip);
                const useSceneOverrides = scope === "scene" && spansMultipleScenes;
                const useAllScope = scope === "all" && spansMultipleScenes;

                    // CaptionsClip: resizing should adjust wrap width (maxWidth), not scale the Pixi container.
                    if (String((clip as any)?.type) === "captions" && typeof (clip as any).updateMaxWidthForScene === "function") {
                        const comp: any = CompositionService.composition as any;
                        const compW = Number(comp?.settings?.width ?? comp?.width ?? 1920);
                        const currentSceneKey = useSceneOverrides ? CompositionService.getCurrentSceneKey() : null;

                        let currentWrap: number | string = (clip as any).maxWidth;
                        try {
                            const overrides = (clip as any).sceneStyleOverrides;
                            const ov = currentSceneKey && overrides && typeof overrides === "object" ? overrides[currentSceneKey] : null;
                            if (ov && ov.maxWidth != null) currentWrap = ov.maxWidth;
                        } catch {}

                        let curPx = 0;
                        if (typeof currentWrap === "number" && Number.isFinite(currentWrap)) curPx = currentWrap;
                        else {
                            const pct = parseFloat(String(currentWrap ?? "80%"));
                            if (Number.isFinite(pct)) curPx = (pct / 100) * (compW || 1920);
                            else curPx = 0.8 * (compW || 1920);
                        }

                        const nextWrapW = Math.max(40, Math.round(curPx + (deltaWidth / pr)));
                        const clamped = Math.min(Math.max(40, compW || nextWrapW), nextWrapW);
                        const sceneKeyForUpdate = useSceneOverrides ? currentSceneKey : null;
                        try {
                            (clip as any).updateMaxWidthForScene(sceneKeyForUpdate, clamped);
                        } catch {
                            try { (clip as any).maxWidth = clamped; } catch {}
                        }

                        CompositionService.composition.computeFrame();
                        handleUpdate();
                        return;
                    }

                    // TextClip: resizing should adjust wrap width (maxWidth), not scale font size via clip.width/height.
                    if (String((clip as any)?.type) === "text" && typeof (clip as any).updateMaxWidthForScene === "function") {
                        let curW = currentW;
                        try {
                            const b = typeof (view as any)?.getBounds === "function" ? (view as any).getBounds() : null;
                            const bw = Number(b?.width ?? Number.NaN);
                            if (Number.isFinite(bw) && bw > 0) curW = bw;
                        } catch {}

                        const nextWrapW = Math.max(40, Math.round(curW + (deltaWidth / pr)));
                        const sceneKey = useSceneOverrides ? CompositionService.getCurrentSceneKey() : null;
                        try {
                            (clip as any).updateMaxWidthForScene(sceneKey, nextWrapW);
                        } catch {
                            try {
                                (clip as any).maxWidth = nextWrapW;
                            } catch {}
                        }

                        CompositionService.composition.computeFrame();
                        handleUpdate();
                        return;
                    }

	                if (useSceneOverrides) {
	                    const sceneKey = CompositionService.getCurrentSceneKey();
	                    if (sceneKey) {
	                        CompositionService.setSceneVisualOverridesForClip(clip, sceneKey, { width: nextW, height: nextH });
	                    } else {
	                        clip.width = nextW;
	                        clip.height = nextH;
	                    }
	                } else {
	                    if (useAllScope && !clearedAllScopeScaleRef.current) {
	                        CompositionService.clearSceneVisualOverrideKeysForClip(clip, ["width", "height"]);
	                        clearedAllScopeScaleRef.current = true;
	                    }
	                    // Regular clip
	                    clip.width = nextW;
	                    clip.height = nextH;
	                }
	            }
            // Re-render the clip so view bounds are updated visually
            CompositionService.composition.computeFrame();
            // Sync the interactive element overlay with new bounds
            handleUpdate();
        } catch {
        }
    };

    // Store initial dimensions at the start of scaling
    const initialSubtitleStateRef = useRef<{ scale: number, width: number, height: number } | null>(null);
    const initialVisualSizeRef = useRef<{ width: number, height: number } | null>(null);

    const handleScaleSubtitles = (deltaWidth: number, deltaHeight: number) => {

    };

    const handleResize = (deltaWidth: number, deltaHeight: number) => {
        return handleScale(deltaWidth, deltaHeight);
    };

    const handleSubtitlesTextWrap = (deltaWidth: number) => {


    };


    const handleRotate = (rotation: number) => {
        const target = getSelectedVisualElement();
        if (!target) return;
        
        const { clip, isScriptLayerSource, sourceIndex } = target;
        const scope = ApplicationStore.sceneEditScope;
        
        // Note: ScriptLayerClip video sources and captions don't support rotation
        if (isScriptLayerSource) return;
        if (clip.type === 'captions') return;
        
        const isVisual = String(clip.type) === 'video' || String(clip.type) === 'image';
        if (!isVisual) return;
        try {
            const deg = (rotation || 0) * 180 / Math.PI;
            const spansMultipleScenes = CompositionService.clipSpansMultipleScenes(clip);
            const useSceneOverrides = scope === "scene" && spansMultipleScenes;
            const useAllScope = scope === "all" && spansMultipleScenes;

            if (useSceneOverrides) {
                const sceneKey = CompositionService.getCurrentSceneKey();
                if (sceneKey) {
                    CompositionService.setSceneVisualOverridesForClip(clip, sceneKey, { rotation: deg });
                } else {
                    clip.rotation = deg;
                }
            } else {
                if (useAllScope) {
                    CompositionService.clearSceneVisualOverrideKeysForClip(clip, ["rotation"]);
                }
                clip.rotation = deg;
            }
            CompositionService.composition.computeFrame();
        } catch {
        }
    };

    const doUpdateSync = useCallback(() => {
        const target = getSelectedVisualElement();
        if (!target) return;
        
        const { view } = target;
        if (!view || typeof view.getBounds !== 'function') return;
        
        try {
            const bounds = view.getBounds();
            const overlayEl = containerRef.current;
            const relativeEl = overlayEl?.parentElement as HTMLElement | null;
            const canvasEl = relativeEl?.querySelector('canvas') as HTMLCanvasElement | null;
            const pr = pixelRatioRef.current;
            if (!Number.isFinite(pr) || pr <= 0) return;
            let originX = 0, originY = 0;
            if (relativeEl && canvasEl) {
                const relRect = relativeEl.getBoundingClientRect();
                const canvasRect = canvasEl.getBoundingClientRect();
                originX = canvasRect.left - relRect.left;
                originY = canvasRect.top - relRect.top;
            }

            const width = (bounds.width || 0) * pr;
            const height = (bounds.height || 0) * pr;
            const x = originX + (bounds.x || 0) * pr;
            const y = originY + (bounds.y || 0) * pr;
            interactionRef?.current?.updateStyle({
                width: `${width}px`,
                height: `${height}px`,
                position: 'absolute',
                top: '0px',
                left: '0px',
                transform: `translate(${x}px, ${y}px)`,
                transformOrigin: '0 0',
                zIndex: `8`,
            });
        } catch {
        }
    }, []);

    // Immediate update for active operations (drag/resize)
    const handleUpdate = useCallback(() => {
        doUpdateSync();
    }, [doUpdateSync]);
    
    // Deferred update for external changes (ensures bounds are fresh after render)
    const handleUpdateDeferred = useCallback(() => {
        requestAnimationFrame(doUpdateSync);
    }, [doUpdateSync]);

    // External callers (canvas resize/scale) can request an overlay refresh.
    useEffect(() => {
        const onUpdateHandlers = () => {
            handleUpdateDeferred();
        };
        window.addEventListener("updateHandlers", onUpdateHandlers);
        return () => window.removeEventListener("updateHandlers", onUpdateHandlers);
    }, [handleUpdateDeferred]);

    const handleDragStart = () => {
        canvasGuidelinesManagerRef.current.prepareGuidelines();
        dragOffsetRef.current = [0, 0];
        clearedAllScopeDragRef.current = false;
    };

    const handleDragEnd = () => {
        canvasGuidelinesManagerRef.current.resetGuidelines();

        // @ts-ignore
        canvasGuidelinesContainerRef.current?.ResetGuidelines();

        // Commit scene visual properties if we were dragging a script layer source
        const target = getSelectedVisualElement();
        if (target?.isScriptLayerSource) {
            const scriptClip = target.clip as ScriptLayerClip;
            try { (scriptClip as any).__librettoAutoDefaultPlacementUserEdited = true; } catch {}
            scriptClip.commitSceneVisualProperties();
            ScriptLayerStore.bumpCutsVersion(); // Full UI update at end
        }

        // Persist + record a single CLIP_UPDATED for the committed transform.
        try {
            if (target?.clip) {
                CompositionService.notifyClipUpdated(target.clip);
            }
        } catch {}
    };

    const handleUpdateSubtitles = () => {
    };

    const handleResizeStart = () => {
        handleScaleStart();
    };

    const handleResizeEnd = () => {
        handleScaleEnd();
    };

    const handleScaleStart = () => {
        const target = getSelectedVisualElement();
        if (!target) return;
        clearedAllScopeScaleRef.current = false;
        
        const { view } = target;
        initialVisualSizeRef.current = {
            width: Number(view?.width ?? 0),
            height: Number(view?.height ?? 0),
        };
    };

    const handleScaleEnd = () => {
        initialVisualSizeRef.current = null;

        // Commit scene visual properties if we were scaling a script layer source
        const target = getSelectedVisualElement();
        if (target?.isScriptLayerSource) {
            const scriptClip = target.clip as ScriptLayerClip;
            try { (scriptClip as any).__librettoAutoDefaultPlacementUserEdited = true; } catch {}
            scriptClip.commitSceneVisualProperties();
            ScriptLayerStore.bumpCutsVersion(); // Full UI update at end
        }

        // Persist + record a single CLIP_UPDATED for the committed transform.
        try {
            if (target?.clip) {
                CompositionService.notifyClipUpdated(target.clip);
            }
        } catch {}
    };

    const handleRotateStart = () => {
    };

    const handleRotateEnd = () => {
        const target = getSelectedVisualElement();
        try {
            if (target?.clip) {
                CompositionService.notifyClipUpdated(target.clip);
            }
        } catch {}
    };

    // Keep a stable Moveable instance; swap handler implementations via refs to avoid orphaned control boxes.
    handlersRef.current.onDrag = handleDrag;
    handlersRef.current.onResize = handleResize;
    handlersRef.current.onScale = handleScale;
    handlersRef.current.onRotate = handleRotate;
    handlersRef.current.dragStart = handleDragStart;
    handlersRef.current.dragEnd = handleDragEnd;
    handlersRef.current.resizeStart = handleResizeStart;
    handlersRef.current.resizeEnd = handleResizeEnd;
    handlersRef.current.scaleStart = handleScaleStart;
    handlersRef.current.scaleEnd = handleScaleEnd;
    handlersRef.current.rotateStart = handleRotateStart;
    handlersRef.current.rotateEnd = handleRotateEnd;

    useEffect(() => {
        const element = containerRef.current;
        if (!element) return;

        const interaction = new InteractiveElement(element, {
            onDrag: (x, y) => handlersRef.current.onDrag(x, y),
            onResize: (dw, dh) => handlersRef.current.onResize(dw, dh),
            onScale: (dw, dh) => handlersRef.current.onScale(dw, dh),
            onRotate: (r) => handlersRef.current.onRotate(r),

            dragStart: () => handlersRef.current.dragStart(),
            dragEnd: () => handlersRef.current.dragEnd(),

            resizeStart: () => handlersRef.current.resizeStart(),
            resizeEnd: () => handlersRef.current.resizeEnd(),

            scaleStart: () => handlersRef.current.scaleStart(),
            scaleEnd: () => handlersRef.current.scaleEnd(),

            rotateStart: () => handlersRef.current.rotateStart(),
            rotateEnd: () => handlersRef.current.rotateEnd(),
        });

        interactionRef.current = interaction;

        return () => {
            interaction.destroy();
            if (interactionRef.current === interaction) interactionRef.current = null;
        };
    }, []);

    useEffect(() => {
        const disposer: IReactionDisposer = reaction(
            () => ({
                currentTime: CompositionStateStore.currentTime,
                selectedClipId: ApplicationStore.selectedClipId,
                selectedVisualElement: ApplicationStore.selectedVisualElement,
                editingTextClipId: ApplicationStore.editingTextClipId,
                canvasResolution: WindowStore.canvasResolution,
                videoResolution: {
                    width: CompositionStateStore.display.width,
                    height: CompositionStateStore.display.height
                },
                windowResolution: WindowStore.resolution,
                isCropMode: ApplicationStore.isCropMode,
            }),
            (payload) => {
                // Hide handlers when in crop mode - CropContainer takes over
                if (payload.isCropMode) {
                    interactionRef.current?.hideHandlers();
                    return;
                }

                if (payload.selectedClipId == null || payload.selectedVisualElement == null) {
                    if (interactionRef.current) {
                        interactionRef.current.hideHandlers();
                    }
                    return;
                }

                // While editing a TextClip, an HTML textarea overlay takes over. Hide Moveable to avoid "phantom" boxes.
                if (payload.editingTextClipId) {
                    interactionRef.current?.hideHandlers();
                    return;
                }

                // Use selectedVisualElement for determining what to show handles for
                const target = getVisualTarget(payload.selectedVisualElement);

                if (!payload.selectedVisualElement || !target) {
                    interactionRef.current?.hideHandlers();
                    return;
                }

                const { clip, view, isScriptLayerSource } = target;

                // Only show handlers when we have a valid canvas scale (prevents "first click" misplacement).
                const [canvasWidth, canvasHeight] = payload.canvasResolution;
                const { width, height } = payload.videoResolution;
                if (!(canvasWidth > 0 && canvasHeight > 0 && width > 0 && height > 0)) {
                    interactionRef.current?.hideHandlers();
                    return;
                }

                // Determine if this visual is currently in the Pixi scene graph.
                // For non-script clips, Track.update() removes clip.view when out of range.
                const isInGraph = !!(view as any)?.parent;
                if (!isInGraph) {
                    interactionRef.current?.hideHandlers();
                    return;
                }

                const clipStart = Number(clip?.start?.seconds ?? 0);
                const clipTimelineDuration = Number((clip as any)?.timelineDuration?.seconds ?? Number.NaN);
                const clipEnd = Number.isFinite(clipTimelineDuration)
                    ? clipStart + Math.max(0, clipTimelineDuration)
                    : Number(clip?.stop?.seconds ?? clipStart);

                const isClipVisibleByTime =
                    payload.currentTime >= clipStart &&
                    payload.currentTime <= clipEnd;

                // Determine if this is a visual element we can interact with
                const isVisual = isScriptLayerSource ||
                    clip.type === 'video' ||
                    clip.type === 'image' ||
                    clip.type === 'captions' ||
                    clip.type === 'text' ||
                    clip.type === 'placeholder';

                // Only show handlers if the clip is visible AND has visual elements
                // Note: captions clips don't support rotation
                const isCaptionsClip = clip.type === 'captions';
                const isTextClip = clip.type === 'text';
                if (isClipVisibleByTime && isVisual) {
                    interactionRef.current?.showHandlers({
                        // Captions and Text clips use dedicated toolbars; keep the box but hide handles.
                        showRotation: !isScriptLayerSource && !isCaptionsClip && !isTextClip,
                        resizable: !isTextClip,
                        scalable: !isTextClip,
                        keepRatio: !isCaptionsClip,
                    });
                } else {
                    interactionRef.current?.hideHandlers();
                    return;
                }

                pixelRatioRef.current = Math.min(canvasWidth / width, canvasHeight / height);

                // Use deferred update to ensure bounds are fresh after any pending renders
                handleUpdateDeferred();
            },
            {fireImmediately: true}
        );

        return () => disposer();
    }, [getSelectedVisualElement, handleUpdate, handleUpdateDeferred]);

    return <>
        <div
            ref={containerRef}
            style={{
                position: 'absolute',
                // top: 0,
                // left: 0,
                // right: 0,
                // bottom: 0,
            }}
        />
        <CanvasAlignmentGuidelinesContainer ref={canvasGuidelinesContainerRef}/>
    </>
});

export default DragResizeRotateContainer;
